import { BaseRowMetadata } from 'apiConnector/secondary-market/batch/base';
import { OrderType, ResponseRowByOrderType as ResponseRow } from 'apiConnector/secondary-market/batch/model';
import type { EditableFieldInfo } from 'components/shared/Blotter/Grid/blotter.model';
import { IBlotterPreferenceState } from 'components/shared/Blotter/Grid/blotter.model';
import { IBlotterColumnDefinition } from 'state/blotters/common/blotter.model';
import { BcsProductKey } from 'state/workspaces/secondary-market/quotes/secondaryMarketQuotes.model';
import { LoadableContent, LoadingStatus } from 'utils/status';

export const ISIN_SEARCH_TYPE = { source: 'ISIN', subtype: undefined } as const;
export const FILE_CODE_SEARCH_TYPE = { source: 'FILE_CODE', subtype: undefined } as const;
export const ELIOT_SEARCH_TYPE = { source: 'ELIOT', subtype: 'ID_WARRANT' } as const;
export const ELIOT_OTC_SEARCH_TYPE = { source: 'ELIOT', subtype: 'ID_FO' } as const;
export const WKN_SEARCH_TYPE = { source: 'WKN', subtype: undefined } as const;
export const VALOREN_SEARCH_TYPE = { source: 'VALOREN', subtype: undefined } as const;
export const CUSIP_SEARCH_TYPE = { source: 'CUSIP', subtype: undefined } as const;
export const XONE_SEARCH_TYPE = { source: 'XONE', subtype: undefined } as const;
export const AVAILABLE_SEARCH_TYPES = [
  ISIN_SEARCH_TYPE,
  FILE_CODE_SEARCH_TYPE,
  ELIOT_SEARCH_TYPE,
  ELIOT_OTC_SEARCH_TYPE,
  WKN_SEARCH_TYPE,
  VALOREN_SEARCH_TYPE,
  CUSIP_SEARCH_TYPE,
  XONE_SEARCH_TYPE,
] as const;
export type SearchType = (typeof AVAILABLE_SEARCH_TYPES)[number];

export type SecondaryBatchColumnDefinitionContext = {
  metadataId?: keyof BaseRowMetadata;
  priceTypeId?: keyof RowData;
  currencyId?: keyof RowData;
  semanticColorsByStatus?: Record<string, string>;
  values?: string[];
};

export type SecondaryBatchColumnDefinition = Omit<IBlotterColumnDefinition<RowData>, 'paths' | 'context'> & {
  context: SecondaryBatchColumnDefinitionContext;
};
export type Row = Omit<ResponseRow, 'metadata'> & {
  query: BcsProductKey;
  data: {
    modifyStatus: LoadingStatus;
    metadata: BaseRowMetadata & { endZonedDateTimeToTrade?: string };
  };
  selected: boolean;
  editableState: Partial<Record<keyof RowData, EditableFieldInfo>>;
};

export type RowData = Row['data'];
export type ValueByOrderType = Row['data'][keyof RowData];

export type SearchCodeType = { code: string; subType?: string };
export type SearchByCodes = {
  primary: SearchCodeType[];
  extended: SearchCodeType[];
};

export type SubStateContentByOrderType = {
  rows: Row[];
  preference?: IBlotterPreferenceState;
  columnsDefinitions: SecondaryBatchColumnDefinition[];
  searchByCodes: SearchByCodes;
};
export type OrderTypeState = {
  searchType: SearchType;
} & LoadableContent<SubStateContentByOrderType, unknown>;

export type SecondaryMarketBatchState = {
  orderType: OrderType;
  stateByOrderType: {
    ['cp-order']: OrderTypeState;
    ['ebiz-quote']: OrderTypeState;
    ['live-quote']: OrderTypeState;
  };
};
export const initialSecondaryMarketBatchState: SecondaryMarketBatchState = {
  orderType: 'cp-order',
  stateByOrderType: {
    'cp-order': { status: 'idle', searchType: { source: 'ISIN', subtype: undefined } },
    'ebiz-quote': { status: 'idle', searchType: { source: 'ISIN', subtype: undefined } },
    'live-quote': { status: 'idle', searchType: { source: 'ISIN', subtype: undefined } },
  },
};
